import { requestPushPermission, isPushSupported, unregisterPush } from '@braze/web-sdk';
import Logger from '@/lib/logger';
import { isBrazeInitialized } from '@/lib/analytics/braze/init';

const logger = new Logger('Braze Push');

/**
 * Check if push notifications are supported in the current browser
 */
export const isPushNotificationSupported = (): boolean => {
  try {
    return isPushSupported();
  } catch (error) {
    logger.error('Error checking push support:', error);
    return false;
  }
};

/**
 * Check if push notifications are already granted
 */
export const isPushNotificationGranted = (): boolean => {
  try {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      return false;
    }
    return Notification.permission === 'granted';
  } catch (error) {
    logger.error('Error checking push permission:', error);
    return false;
  }
};

/**
 * Request permission for push notifications
 * This will show the browser's native permission prompt
 *
 * IMPORTANT: In Safari and Firefox, this must be called from a short-lived event handler (e.g., button click) due to browser security requirements.
 *
 * @returns Promise that resolves when permission is requested
 */
export const requestPushNotificationPermission = async (): Promise<void> => {
  if (!isBrazeInitialized()) {
    logger.warn('Cannot request push permission: Braze SDK is not initialized or disabled');
    return;
  }

  if (!isPushNotificationSupported()) {
    logger.warn('Push notifications are not supported in this browser');
    return;
  }

  if (isPushNotificationGranted()) {
    logger.info('Push notifications are already granted');
    return;
  }

  try {
    logger.info('Requesting push notification permission');
    requestPushPermission();
  } catch (error) {
    logger.error('Failed to request push permission:', error);
  }
};

/**
 * Unsubscribe a user from push notifications
 *
 * IMPORTANT: In Safari and Firefox, this must be called from a short-lived event handler (e.g., button click) due to browser security requirements.
 */
export const unsubscribeFromPushNotifications = async (): Promise<void> => {
  if (!isBrazeInitialized()) {
    logger.warn('Cannot unsubscribe from push: Braze SDK is not initialized or disabled');
    return;
  }

  if (!isPushNotificationSupported()) {
    logger.warn('Push notifications are not supported in this browser');
    return;
  }

  if (!isPushNotificationGranted()) {
    logger.info('Push notifications are not granted, nothing to unsubscribe');
    return;
  }

  try {
    logger.info('Unsubscribing from push notifications');
    unregisterPush();
    logger.info('Successfully unsubscribed from push notifications');
  } catch (error) {
    logger.error('Failed to unsubscribe from push notifications:', error);
  }
};
